import React, { useEffect, useState } from "react";
import LandingNav from "../../components/LandingNav";

const ASSET_BASE = `${process.env.REACT_APP_UPLOADED_URL}/realestate/landing`;

const VALUE_PROPS = [
  { title: "Virtual Project Demos", text: "Showcase developments before they're built using VR sky-walks and 360° tours." },
  { title: "Project Collaboration", text: "Onboard your agencies, agents, and sales teams onto one Web 3.0 platform." },
  { title: "3D Model Scale", text: "Bring 3D model walkthroughs to investors so they can experience future spaces today." },
  { title: "Extrapolate Projects", text: "Turn every project into a digital tour and toolkit for sales acceleration." },
];

const HOW_WE_HELP = [
  {
    title: "Digital Project Environment",
    text: "Showcase every project via sky-walk, drive view, and 360 walkthroughs — for both built and unbuilt spaces.",
    img: `${ASSET_BASE}/feature-card-3.png`,
    position: "left" as const,
  },
  {
    title: "Project Showcase Management",
    text: "Onboard projects, agencies, agents and sales teams onto our Web 3.0 platform and present to potential clients from the comfort of their home or office.",
    img: `${ASSET_BASE}/feature-card-1.png`,
    position: "right" as const,
  },
  {
    title: "3D Model Walkthrough for Investors",
    text: "Create immersive 3D walkthroughs for unbuilt spaces — helping buyers and investors visualize future developments before construction begins.",
    img: `${ASSET_BASE}/feature-card-2.png`,
    position: "left" as const,
  },
];

const SPECS = [
  "134-megapixel 3D scans",
  "99% accurate 3D floor plans",
  "100-meter scanning range",
  "±20mm depth accuracy at 10m",
  "30-second full 360° scan",
  "4K HDR video support",
];

const OFFERINGS = [
  {
    title: "Architectural Support & 3D Visualizations",
    badge: "To be built",
    text: "We assist developers with architectural drawings and create 3D walkthroughs for investors and buyers to envision the finished space.",
    price: "Custom quote",
  },
  {
    title: "Skywalk Overview",
    badge: "To be built",
    text: "Develop skywalk views showcasing your project's location and surroundings.",
    price: "USD 1,000 – 2,000 / acre",
  },
  {
    title: "3D Walkview & Driveview Videos",
    badge: "Currently building",
    text: "Virtual walkthroughs that eliminate the need for on-site visits.",
    price: "USD 100 – 1,000",
  },
];

const Builders: React.FC = () => {
  const [form, setForm] = useState({ name: "", email: "", mobile: "", project: "" });
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Poppins:wght@400;500;600&display=swap";
    document.head.appendChild(link);

    const prev = document.body.style.overflow;
    document.body.style.overflow = "auto";
    return () => { document.body.style.overflow = prev; };
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: wire this submission to the leads backend. Currently only updates
    // local state — no email, API call, or persistence. Do not enable this
    // page publicly until backed by a real endpoint.
    console.warn("[Builders] Inquiry form is not wired to a backend yet. Payload:", form);
    setSubmitted(true);
  };

  return (
    <div style={{ fontFamily: "'Poppins', sans-serif" }} className="text-[#2b2c2d]">

      <LandingNav active="builders" />

      {/* 1. HERO */}
      <section
        className="relative w-full h-[80vh] min-h-[520px] flex items-center justify-center text-center px-6"
        style={{
          backgroundImage: `url(${ASSET_BASE}/section-bg-2.jpg)`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-black/55" />
        <div className="relative z-10 max-w-3xl">
          <h1 style={{ fontFamily: "'DM Sans', sans-serif" }} className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
            Solutions for Builders.
          </h1>
          <p className="text-base md:text-xl text-white/85 mb-10">
            Find the best selling point for your projects — those being built, or yet to be built — with our VR solutions and platform.
          </p>
          <a
            href="#inquiry"
            className="inline-block px-8 py-3 rounded-[6px] bg-[#b7aca2] text-[#262626] text-sm font-semibold hover:bg-[#a89d93] transition-colors"
          >
            Talk to us
          </a>
        </div>
      </section>

      {/* 2. VALUE PROPS - 4 COL ON WHITE */}
      <section className="bg-white py-16 px-6">
        <div className="max-w-[1200px] mx-auto">
          <h2 style={{ fontFamily: "'DM Sans', sans-serif" }} className="text-3xl md:text-4xl font-bold text-[#2b2c2d] text-center mb-3">
            Extrapolate every project into a digital tour
          </h2>
          <p className="text-[#797068] text-center text-sm md:text-base max-w-2xl mx-auto mb-12">
            Access your data in one place, gain actionable insights, and unlock a new dimension of teamwork.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {VALUE_PROPS.map((v) => (
              <div key={v.title}>
                <h3 style={{ fontFamily: "'DM Sans', sans-serif" }} className="font-bold text-[#262626] text-base mb-3">{v.title}</h3>
                <p className="text-[#2b2c2d] text-sm leading-relaxed">{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3. HOW DO WE HELP - ALTERNATING ROWS */}
      <section className="bg-white pb-16 px-6">
        <div className="max-w-[1200px] mx-auto">
          <h2 style={{ fontFamily: "'DM Sans', sans-serif" }} className="text-3xl md:text-4xl font-bold text-[#2b2c2d] text-center mb-12">
            How do we help Builders?
          </h2>
          <div className="flex flex-col gap-16">
            {HOW_WE_HELP.map((row) => (
              <div
                key={row.title}
                className={`flex flex-col gap-10 items-center ${row.position === "left" ? "md:flex-row" : "md:flex-row-reverse"}`}
              >
                <div className="md:w-1/2">
                  <img src={row.img} alt={row.title} className="w-full rounded-[8px] object-cover" />
                </div>
                <div className="md:w-1/2">
                  <h3 style={{ fontFamily: "'DM Sans', sans-serif" }} className="text-2xl md:text-3xl font-bold text-[#2b2c2d] mb-4">
                    {row.title}
                  </h3>
                  <p className="text-[#2b2c2d] text-sm md:text-base leading-relaxed">{row.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. INNOVATION BANNER - DARK BG */}
      <section
        className="relative py-24 px-6"
        style={{
          backgroundImage: `url(${ASSET_BASE}/section-bg-1.jpg)`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
        }}
      >
        <div className="absolute inset-0 bg-[#1c2228]/75" />
        <div className="relative z-10 max-w-[1200px] mx-auto text-center">
          <h2 style={{ fontFamily: "'DM Sans', sans-serif" }} className="text-3xl md:text-5xl font-bold text-white mb-6">
            Innovation in service to Real Estate
          </h2>
          <p className="text-white/80 max-w-3xl mx-auto text-sm md:text-base leading-relaxed mb-10">
            High-level performance and advanced features — AI-powered VR scans and models, high-precision real-time tracking,
            and 360° vision immersiveness.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-3xl mx-auto">
            {SPECS.map((s) => (
              <div key={s} className="text-white/90 text-sm bg-white/10 border border-white/20 rounded-[6px] px-4 py-3">
                {s}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. META-QUEST */}
      <section className="bg-white py-16 px-6">
        <div className="max-w-[1200px] mx-auto flex flex-col md:flex-row gap-10 items-center">
          <div className="md:w-1/2">
            <img
              src={`${ASSET_BASE}/product-hero-3.webp`}
              alt="Meta Quest"
              className="w-full rounded-[8px]"
            />
          </div>
          <div className="md:w-1/2">
            <p className="text-xs uppercase tracking-widest text-[#797068] mb-2">With latest Meta-Quest technology</p>
            <h2 style={{ fontFamily: "'DM Sans', sans-serif" }} className="text-3xl font-bold text-[#2b2c2d] mb-4">
              Real-time 360° virtual environments
            </h2>
            <p className="text-[#2b2c2d] text-sm leading-relaxed">
              Matterport Pro2 / Pro3 cameras power our digital twins for real estate, construction, and retail —
              with 134 MP scans, 100m range, and ±20mm depth accuracy at 10 meters.
            </p>
          </div>
        </div>
      </section>

      {/* 6. DIGITAL TWINS */}
      <section className="bg-white pb-16 px-6">
        <div className="max-w-[1200px] mx-auto text-center">
          <h2 style={{ fontFamily: "'DM Sans', sans-serif" }} className="text-3xl md:text-4xl font-bold text-[#2b2c2d] mb-4">
            Digital twins of properties on our platform
          </h2>
          <p className="text-[#797068] text-sm md:text-base mb-3">Let's experience the VR digital twin captured with our camera.</p>
          <p className="text-[#2b2c2d] text-sm md:text-base leading-relaxed max-w-3xl mx-auto">
            For spaces already built we leverage Matterport's 3D VR digital twins to revolutionize property sales —
            immersive, interactive virtual tours that give buyers a realistic, in-depth view from anywhere in the world.
            The result: higher engagement, faster decisions, and stronger sales conversions.
          </p>
        </div>
      </section>

      {/* 7. OFFERINGS / PRICING */}
      <section className="bg-[#f6f3ef] py-16 px-6">
        <div className="max-w-[1200px] mx-auto">
          <h2 style={{ fontFamily: "'DM Sans', sans-serif" }} className="text-3xl md:text-4xl font-bold text-[#2b2c2d] text-center mb-12">
            What we offer Builders
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {OFFERINGS.map((o) => (
              <div key={o.title} className="bg-white rounded-[8px] p-6 flex flex-col border border-[#e5dfd6]">
                <span className="self-start text-xs uppercase tracking-widest text-[#b7aca2] font-semibold mb-3">{o.badge}</span>
                <h3 style={{ fontFamily: "'DM Sans', sans-serif" }} className="font-bold text-[#262626] text-lg mb-3">{o.title}</h3>
                <p className="text-[#2b2c2d] text-sm leading-relaxed mb-6 flex-1">{o.text}</p>
                <p className="text-[#262626] text-sm font-semibold">{o.price}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 8. TESTIMONIAL */}
      <section className="bg-white py-20 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-xs uppercase tracking-widest text-[#797068] mb-4">Testimonials</p>
          <h2 style={{ fontFamily: "'DM Sans', sans-serif" }} className="text-2xl md:text-3xl font-bold text-[#2b2c2d] mb-6 leading-snug">
            "Since using OpenSky's digital twins, our property listings have seen a 40% increase in engagement.
            Buyers love the immersive experience — and we're closing deals faster than ever."
          </h2>
          <p className="text-[#797068] text-sm">— Sarah M., Real Estate Agent</p>
        </div>
      </section>

      {/* 9. INQUIRY FORM */}
      <section id="inquiry" className="bg-[#1c2228] py-20 px-6">
        <div className="max-w-2xl mx-auto">
          <h2 style={{ fontFamily: "'DM Sans', sans-serif" }} className="text-3xl md:text-4xl font-bold text-white text-center mb-4">
            Get in touch
          </h2>
          <p className="text-white/70 text-center text-sm md:text-base mb-6">
            Tell us about your project and we'll respond within one business day.
          </p>
          {/* TODO: remove this banner once the form is wired to the leads backend */}
          <div className="bg-yellow-500/10 border border-yellow-500/40 text-yellow-200 text-xs rounded-[6px] px-4 py-2 mb-8 text-center">
            Heads up — this inquiry form is not yet connected to a backend. Submissions are logged to the browser console only.
          </div>
          {submitted ? (
            <div className="bg-white/10 border border-white/20 rounded-[8px] p-6 text-center text-white">
              Thanks — we'll be in touch shortly.
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <input
                type="text"
                required
                placeholder="Your name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="bg-white/10 border border-white/20 rounded-[6px] px-4 py-3 text-white placeholder-white/50 text-sm focus:outline-none focus:border-white/60"
              />
              <input
                type="email"
                required
                placeholder="Email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="bg-white/10 border border-white/20 rounded-[6px] px-4 py-3 text-white placeholder-white/50 text-sm focus:outline-none focus:border-white/60"
              />
              <input
                type="tel"
                required
                placeholder="Mobile number"
                value={form.mobile}
                onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                className="bg-white/10 border border-white/20 rounded-[6px] px-4 py-3 text-white placeholder-white/50 text-sm focus:outline-none focus:border-white/60"
              />
              <textarea
                placeholder="Project details — location, size, timeline"
                value={form.project}
                onChange={(e) => setForm({ ...form, project: e.target.value })}
                rows={4}
                className="bg-white/10 border border-white/20 rounded-[6px] px-4 py-3 text-white placeholder-white/50 text-sm focus:outline-none focus:border-white/60"
              />
              <button
                type="submit"
                className="self-center mt-2 px-10 py-3 rounded-[6px] bg-[#b7aca2] text-[#262626] text-sm font-semibold hover:bg-[#a89d93] transition-colors"
              >
                Submit
              </button>
            </form>
          )}
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#1c2228] py-10 px-6 border-t border-white/10">
        <div className="max-w-[1200px] mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <img src="/images/logo.svg" alt="OpenSky" className="h-10 w-auto" />
          <div className="flex gap-6 text-sm text-white/60">
            <a href="/privacy_policy" className="hover:text-white transition-colors">Privacy Policy</a>
            <a href="/terms_and_conditions" className="hover:text-white transition-colors">Terms & Conditions</a>
          </div>
          <p className="text-white/60 text-xs">© {new Date().getFullYear()}, OpenSky VR RE Platform</p>
        </div>
      </footer>

    </div>
  );
};

export default Builders;
